import { useEffect, useRef, useState, type JSX } from 'react';
import type { EffectProps } from './types';
import { markEffectActivated, isEffectActivated } from './effectRegistry';

/* ── 機械語キャラクタープール ── */
const BIN   = '01';
const HEX   = '0123456789ABCDEF';
const BLOCK = '░▒▓█▀▄■□';

function machineChar(seed: number): string {
  const s = Math.abs(seed);
  const bucket = s % 10;
  if (bucket < 5) return BIN[s % BIN.length];
  if (bucket < 8) return HEX[s % HEX.length];
  return BLOCK[s % BLOCK.length];
}

/** 指定長の機械語文字列を生成（毎フレーム変化する） */
function buildMachineText(length: number, time: number): string {
  let out = '';
  for (let i = 0; i < length; i++) {
    // 8文字ごとにスペースを入れてバイナリダンプ感を出す
    if (i > 0 && i % 8 === 0) out += ' ';
    out += machineChar(i * 13 + Math.floor(time / 50));
  }
  return out;
}

type Phase = 'idle' | 'flicker' | 'burst' | 'persist';

export function TextCorruption({
  originalContent,
  params,
  trigger,
  triggerDelayMs,
  onComplete,
  children,
  effectKey,
}: EffectProps): JSX.Element {
  // リマウント時: 既に発動済みなら即座にpersistフェーズから開始
  const alreadyActivated = effectKey ? isEffectActivated(effectKey) : false;
  const [phase, setPhase] = useState<Phase>(alreadyActivated ? 'persist' : 'idle');
  const [displayText, setDisplayText] = useState<string | null>(null);
  const rafRef = useRef(0);

  /* パラメータ */
  const startDelay   = (params.startDelayMs      as number) ?? 500;
  const flickerMs    = (params.flickerDurationMs  as number) ?? 800;
  const burstMs      = (params.burstDurationMs    as number) ?? 600;
  const expansion    = (params.expansionFactor    as number) ?? 4;

  /* ① トリガー → フリッカー開始（未発動時のみ） */
  useEffect(() => {
    if (alreadyActivated) return;
    const delay = (trigger === 'after_delay' ? triggerDelayMs : 0) + startDelay;
    const t = setTimeout(() => setPhase('flicker'), delay);
    return () => clearTimeout(t);
  }, [trigger, triggerDelayMs, startDelay, alreadyActivated]);

  /* ② フリッカー → バースト開始 */
  useEffect(() => {
    if (phase !== 'flicker') return;
    const t = setTimeout(() => setPhase('burst'), flickerMs);
    return () => clearTimeout(t);
  }, [phase, flickerMs]);

  /* ③ バースト：機械語が一気に溢れ出す */
  useEffect(() => {
    if (phase !== 'burst') return;
    const baseLen = originalContent.text.length;
    const targetLen = Math.max(baseLen * expansion, 40);
    const start = performance.now();

    const animate = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / burstMs, 1);
      // 表示文字数が急激に増加（easeOutで一気に出る感じ）
      const eased = 1 - (1 - progress) * (1 - progress);
      const visibleLen = Math.floor(eased * targetLen);
      setDisplayText(buildMachineText(visibleLen, now));

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        setPhase('persist');
        if (effectKey) markEffectActivated(effectKey);
        onComplete?.();
      }
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [phase, originalContent.text, burstMs, expansion, onComplete, effectKey]);

  /* ④ 永続：文字がずっとサイクルし続ける（バグった状態が継続） */
  useEffect(() => {
    if (phase !== 'persist') return;
    const baseLen = originalContent.text.length;
    const targetLen = Math.max(baseLen * expansion, 40);

    // 発動記録（リマウント経由で来た場合も記録）
    if (effectKey) markEffectActivated(effectKey);

    const animate = (now: number) => {
      setDisplayText(buildMachineText(targetLen, now));
      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [phase, originalContent.text, expansion, effectKey]);

  /* ── レンダー ── */
  if (phase === 'idle') return <>{children}</>;

  /* フリッカーのみ（まだバースト前） */
  if (phase === 'flicker') {
    return (
      <div className="sn-corruption-wrap sn-corruption-flicker">
        {children}
      </div>
    );
  }

  /* バースト＆永続：機械語テキスト表示 */
  return (
    <div className={`sn-corruption-wrap ${phase === 'burst' ? 'sn-corruption-flicker' : ''}`}>
      <div className="sn-corrupted-bubble">
        <span className="sn-corrupted-text">{displayText}</span>
      </div>
    </div>
  );
}
